/**
 * Map layer registry (CLARID-UX-2026-06, PR 8 — mapping foundation).
 *
 * The single catalog of every layer the unified `/mapping` engine will support,
 * from epic Appendix D. Each layer declares its data source and the feature that
 * gates it, so the engine can offer one layer selector that serves Core →
 * Enterprise (per-layer gating, not whole-page gating). PR 9 wires these to real
 * data + the layer selector; PR 10 extends them. Foundation only — metadata, no
 * rendering yet.
 *
 * `feature` keys match `featureGate.service.ts`. `undefined` = base canvas
 * (available to any tier that has mapping).
 */

export type MapLayerKind = 'fill' | 'line' | 'circle' | 'marker' | 'heatmap' | 'symbol';

export type MapLayerGroup = 'base' | 'fair-lending' | 'cra' | 'marketing' | 'bonds';

export interface MapLayerDef {
  /** Stable id used by the layer selector and Mapbox layer id. */
  id: string;
  label: string;
  description: string;
  kind: MapLayerKind;
  group: MapLayerGroup;
  /** Feature gate; `undefined` = available on the base canvas. */
  feature?: string;
  /** Which dataset/endpoint feeds this layer (resolved by the page in PR 9). */
  source: string;
}

/** Appendix D catalog. Keyed by layer id. */
export const MAP_LAYERS: Record<string, MapLayerDef> = {
  'tract-income': {
    id: 'tract-income',
    label: 'Census tracts — income tier (LMI)',
    description: 'Tracts shaded Low / Moderate / Middle / Upper.',
    kind: 'fill',
    group: 'base',
    source: 'ffiec-tract-reference',
  },
  'tract-mmct': {
    id: 'tract-mmct',
    label: 'Census tracts — minority concentration (MMCT)',
    description: 'Tracts shaded by the regulator-aware MMCT classifier.',
    kind: 'fill',
    group: 'base',
    source: 'ffiec-tract-reference',
  },
  'assessment-areas': {
    id: 'assessment-areas',
    label: 'Assessment-area outlines',
    description: "Your CRA assessment-area boundaries.",
    kind: 'line',
    group: 'cra',
    feature: 'module_cra',
    source: 'cra-assessment-areas',
  },
  'county-names': {
    id: 'county-names',
    label: 'County names',
    description:
      'County names over the mapped area. Delineated counties appear in capitals, matching the R9 tables.',
    kind: 'symbol',
    group: 'cra',
    feature: 'module_cra',
    source: 'cra-assessment-areas',
  },
  'ad-reach': {
    id: 'ad-reach',
    label: 'Ad reach',
    description: 'Tracts your marketing reached (vs not).',
    kind: 'fill',
    group: 'marketing',
    // Relocated from Fair Lending to Marketing per the epic.
    feature: 'module_marketing_compliance',
    source: 'ad-distribution',
  },
  'loan-dots': {
    id: 'loan-dots',
    label: 'Loan dots',
    description: 'Originated / denied / withdrawn / purchased applications.',
    kind: 'circle',
    group: 'fair-lending',
    feature: 'fair_lending',
    source: 'hmda-lar-or-alert',
  },
  branches: {
    id: 'branches',
    label: 'Branch / LPO markers',
    description: 'Main office, branches, and loan/deposit production offices.',
    kind: 'marker',
    group: 'fair-lending',
    feature: 'fair_lending',
    source: 'team-branches',
  },
  redlining: {
    id: 'redlining',
    label: 'Redlining shading',
    description: 'Redlining-analysis output shading.',
    kind: 'fill',
    group: 'fair-lending',
    feature: 'redlining_analysis',
    source: 'redlining-analysis',
  },
  'sb-density': {
    id: 'sb-density',
    label: 'Small-business density',
    description: 'SB establishment universe per tract (quartile heatmap).',
    kind: 'fill',
    group: 'cra',
    feature: 'module_cra',
    source: 'sb-density',
  },
  'credit-penetration': {
    id: 'credit-penetration',
    label: 'Credit penetration',
    description: 'SB lending per tract relative to the AA mean.',
    kind: 'fill',
    group: 'cra',
    feature: 'module_cra',
    source: 'cra-aggregate-a1',
  },
  'bond-properties': {
    id: 'bond-properties',
    label: 'Bond property markers',
    description: 'Properties colored by CRA-qualification status.',
    kind: 'circle',
    group: 'bonds',
    feature: 'module_bonds',
    source: 'bond-qualification',
  },
};

export const mapLayerList: MapLayerDef[] = Object.values(MAP_LAYERS);

/** Layers available given the set of features the current plan/team grants. */
export function availableLayers(hasFeature: (feature: string) => boolean): MapLayerDef[] {
  return mapLayerList.filter((layer) => !layer.feature || hasFeature(layer.feature));
}
